import { Button, Form, Input, Spin } from 'antd';
import { Icon } from '../components/Core/Icon';
import useLogin from '../hooks/useLogin';
import { usePermissions } from '../hooks/usePermissions';
import { getBrandingDataDefaultImage } from '../utils/utils';
import clsx from 'clsx';

export const Login = () => {
  const {
    isLoading,
    setForm,
    onSubmit,
    form,
    t,
    isOtpRequested,
    otpTimer,
    handleRequestOtp,
    selectedFile,
    handleRemoveFile,
    isLoggedIn,
    isLoadingOverlayVisible,
    isEmailValid,
    authenticate,
    isPageLoaded,
    otpInputRef,
  } = useLogin();
  const { brandingData, isLoadingBrandingData } = usePermissions();

  return (
    <>
      {isLoadingOverlayVisible && (
        <div className="absolute inset-0 -top-20 z-50 flex items-center justify-center bg-white transition-all duration-200">
          <div className="flex flex-row items-center justify-center gap-2">
            <span>Verifying file...</span>
            <Spin />
          </div>
        </div>
      )}

      <div className="flex w-full max-w-md flex-col items-center justify-center">
        <img
          className={clsx(
            'max-w-[200px] transition-all duration-300 ease-in-out',
            (isLoadingBrandingData || !isPageLoaded) && 'opacity-25 blur-lg'
          )}
          loading="eager"
          src={
            brandingData?.loginPageLogo
              ? `${brandingData.loginPageLogo}?v=${Date.now()}`
              : getBrandingDataDefaultImage('login-page')
          }
          alt={brandingData?.companyName || 'Filegrant Enterprise'}
        />
        <h1
          className="pb-1 pt-4 text-center leading-6"
          dangerouslySetInnerHTML={{
            __html: brandingData?.loginPageTitle || t('auth.loginPageTitle'),
          }}
        />
        <h2
          className="text-center leading-6"
          dangerouslySetInnerHTML={{
            __html:
              brandingData?.loginPageSubtitle || t('auth.loginPageSubtitle'),
          }}
        />
        <Form
          onFinish={onSubmit}
          className="grid w-full grid-cols-1 pt-4 transition-all duration-200 [&_.ant-form-item-explain-error]:hidden [&_.ant-form-item-label]:hidden [&_.ant-form-item]:mb-2"
        >
          {selectedFile && (
            <div className="mb-4 rounded-md border border-blue-dark px-4 py-3">
              <label className="flex items-center justify-between text-blue-dark">
                <span>
                  {isLoggedIn
                    ? selectedFile?.fileProtected?.originaleFileName
                    : 'File Shared'}
                </span>
                {!isLoading && (
                  <div className="" onClick={handleRemoveFile}>
                    <Icon name="XIcon" className="h-5 w-5" />
                  </div>
                )}
              </label>
            </div>
          )}
          <Form.Item
            label="email"
            className="rounded-md border border-gray pb-2"
            rules={[{ required: true, type: 'email' }]}
          >
            <label className="px-2.5 text-sm text-blue-dark">
              {brandingData?.loginPageEmailLabel ||
                t('auth.loginPageEmailLabel')}
            </label>
            <Input
              type="email"
              variant="borderless"
              size="small"
              autoFocus
              placeholder={
                brandingData?.loginPageEmailPlaceholder ||
                t('auth.loginPageEmailPlaceholder')
              }
              disabled={isLoggedIn}
              required
              value={form.email}
              pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
              onChange={(e) =>
                setForm((prevForm) => ({
                  ...prevForm,
                  email: e.target.value,
                }))
              }
            />
          </Form.Item>
          {!isLoggedIn && isOtpRequested && (
            <div className="flex flex-col pt-2 transition-all">
              <div className="flex flex-row items-center justify-start">
                <span>
                  {brandingData?.loginPageOtpLabel ||
                    t('auth.loginPageOtpLabel')}
                </span>
              </div>
              <Form.Item
                name="code"
                className="mb-0 w-full pb-0 pt-4"
                rules={[{ required: true, type: 'string', min: 6, max: 6 }]}
              >
                <Input.OTP
                  size="large"
                  ref={otpInputRef}
                  className="w-full p-0"
                  inputMode="numeric"
                  disabled={otpTimer ? otpTimer > 0 : false || isLoading}
                  value={form.code}
                  separator={(i) => <span>-</span>}
                  onChange={(e) => {
                    setForm((prevForm) => ({
                      ...prevForm,
                      code: e,
                    }));
                    authenticate(e).then(() => {});
                  }}
                />
              </Form.Item>
              <div className="mt-2 flex flex-row justify-end">
                <Button
                  type="link"
                  size="small"
                  onClick={handleRequestOtp}
                  className="text-sm"
                  disabled={otpTimer ? otpTimer > 0 : false}
                >
                  {brandingData?.loginPageResendOtp ||
                    t('auth.loginPageResendOtp')}
                  {otpTimer &&
                    `
                  (${otpTimer})
                    `}
                </Button>
              </div>
            </div>
          )}

          <p className="pt-4 text-center text-sm">
            <span
              dangerouslySetInnerHTML={{
                __html:
                  brandingData?.loginTermsAndConditions ||
                  t('auth.loginTermsAndConditions'),
              }}
            />
          </p>

          <Button
            type="primary"
            size="large"
            htmlType="submit"
            className="mt-4 w-full"
            disabled={
              (isOtpRequested && (!form.code || form.code?.length !== 6)) ||
              !isEmailValid
            }
            loading={isLoading}
          >
            {brandingData?.loginButtonLabel || t('auth.loginButtonLabel')}
          </Button>
        </Form>
      </div>
    </>
  );
};

export default Login;
